import { useContext, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import cx from 'classnames';
import { StoreContext, namespace, ActionTypeValues } from '@/context';
import { PromiseState } from '@/constants';
import { IThreadLisItem } from '@/api/thread/list';
import { formatDisplayNumber } from '@/utils/formatNumber';
import Toast from '@/components/Toast';
import DefaultAvatar from '../../assets/icon/account/def_avatar.webp';
import styles from './index.module.scss';
import { getUserCenterInitInfo, getUserLoginInfo, IUserInfo, IUserLoginInfo, ACCOUNT_ROLE } from '@/api/user';
import { getNavsList, ITopLevelNav } from '@/api/nav';
import { getUserEquipments, IUserEquipment } from '@/api/device';

// 页面专属 hooks / 渲染器 / 类型
import useSls from '@/hooks/useSls';
import useUserCenterSlsReport, { ILoginInfoSlsPayload } from '@/features/user-center/useUserCenterSlsReport';
import { parseUidFromQuery, resolveReportUid, resolveUserCenterCreditTip } from '@/utils/userCenter';
import { getUserCenterPcServerSideProps } from '@/features/user-center/pc/ssr';
import { USER_CENTER_MAX_POINTS_VALUE } from '@/features/user-center/pc/constants';
import { isPcTabContentEmpty, renderPcTabContent } from '@/features/user-center/pc/renderers';
import UserCenterPcLayout from './pc/PageLayout';
import { useUserCenterPcListLogic } from '@/features/user-center/pc/useUserCenterPcListLogic';
import { useUserCenterPcActions } from '@/features/user-center/pc/useUserCenterPcActions';
import { IUserCenterProps } from '@/features/user-center/pc/types';

const UserCenter = ({ initialWorks = [], userStats, initialAccount, initialUserInfo, initialDevices = [], navList, saltkey, initialSsrDuration = 0, initialLoginInfoSls = null }: IUserCenterProps) => {
  // 页面级上下文与全局状态入口：i18n、路由、全局 store、埋点与日志上报
  const t = useTranslations();
  const { state, dispatch } = useContext(StoreContext);
  const router = useRouter();
  // 全局 store 中挂载了 account/sensors/permission 等页面运行依赖
  const { account: storeAccount, sensors, permission } = state[namespace.app];
  // 神策埋点能力
  const { track, TRACK_EVENTS } = sensors || {};
  // SLS 上报能力
  const { reportSlsData } = useSls();

  // UI 与交互状态：tab、各弹窗、资料 patch、本地可见性开关
  const [activeTab, setActiveTab] = useState('myPosts');
  const [isTabInitializedFromQuery, setIsTabInitializedFromQuery] = useState(false);
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [showPointsExchangeModal, setShowPointsExchangeModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [profilePatch, setProfilePatch] = useState<{ name?: string; avatarUrl?: string } | null>(null);
  const [badgeIconError, setBadgeIconError] = useState(false);
  const [hideDistance, setHideDistance] = useState(false);
  const [updating, setUpdating] = useState(false);
  // 只提示一次积分 toast，避免重复弹出干扰体验
  const hasShownCreditTipRef = useRef(false);
  // 每个 tab 的滚动快照（用于切 tab 后恢复）
  const tabScrollStateMapRef = useRef<Record<string, { top: number; offsetFromBottom: number; isBottomAnchored: boolean }>>({});
  // 记录前一个 tab，辅助判断是否需要恢复滚动
  const prevActiveTabRef = useRef(activeTab);
  // 程序化滚动期间阻断“触底自动加载”
  const programmaticScrollRef = useRef(false);
  const programmaticScrollTimerRef = useRef<number | null>(null);
  // tab 恢复滚动后，在用户真实滚动前先抑制自动加载
  const suppressAutoLoadUntilUserScrollRef = useRef(false);
  // 保存刚恢复到的 scrollTop，便于判定“是否用户主动滚动”
  const restoredScrollTopRef = useRef<number | null>(null);
  // 帖子局部状态 patch：点赞/收藏等本地修改先写到这里，再叠加到列表
  const threadStatePatchRef = useRef<Record<string, Partial<IThreadLisItem>>>({});
  // 账户信息优先级：先用 store 的已完成态，再回退 SSR 注入数据
  const account = useMemo(() => {
    if (storeAccount?.status === PromiseState.FULFILLED) return storeAccount as IUserLoginInfo;
    return (initialAccount as IUserLoginInfo) || storeAccount;
  }, [storeAccount, initialAccount]);
  // URL 中 uid 标准化，后续统一用于“本人/访客视角”判定
  const routeUid = useMemo(() => parseUidFromQuery(router.query.uid), [router.query.uid]);
  // 本人/访客视角判定规则：
  // 1) URL 有 uid 时：登录 id 与 uid 相等即本人
  // 2) URL 无 uid 时：回退与 initialUserInfo.id 对比
  // 3) 都不可用时：默认本人（兼容历史入口）
  const isSelfView = useMemo(() => {
    const loginId = Number(account?.id);
    if (!Number.isFinite(loginId)) return false;
    if (Number.isFinite(routeUid)) {
      return loginId === routeUid;
    }
    if (Number.isFinite(Number(initialUserInfo?.id))) {
      return String(loginId) === String(initialUserInfo?.id);
    }
    return true;
  }, [account?.id, routeUid, initialUserInfo?.id]);

  // 固定滚动容器表现，避免切 tab 或恢复滚动时因 gutter 抖动导致偏移
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    const nextElement = document.getElementById('__next');
    const htmlElement = document.documentElement;
    const prevNextScrollbarGutter = nextElement?.style.getPropertyValue('scrollbar-gutter');
    const prevHtmlScrollbarGutter = htmlElement.style.getPropertyValue('scrollbar-gutter');

    if (nextElement) {
      nextElement.style.setProperty('scrollbar-gutter', 'stable');
    }

    return () => {
      if (nextElement) {
        if (prevNextScrollbarGutter) {
          nextElement.style.setProperty('scrollbar-gutter', prevNextScrollbarGutter);
        } else {
          nextElement.style.removeProperty('scrollbar-gutter');
        }
      }
      if (prevHtmlScrollbarGutter) {
        htmlElement.style.setProperty('scrollbar-gutter', prevHtmlScrollbarGutter);
      } else {
        htmlElement.style.removeProperty('scrollbar-gutter');
      }
    };
  }, []);

  // 统一读取页面滚动指标，兼容 __next 容器与 window 两种滚动来源
  const getPageScrollMetrics = useCallback(() => {
    if (typeof window === 'undefined') {
      return { scrollTop: 0, clientHeight: 0, scrollHeight: 0, maxScrollTop: 0 };
    }
    // 同时兼容 `__next` 容器滚动与 window 滚动（不同布局下都可工作）
    const nextElement = document.getElementById('__next');
    const scrollTop = nextElement?.scrollTop || window.scrollY || document.documentElement.scrollTop || 0;
    const clientHeight = nextElement?.clientHeight || window.innerHeight || document.documentElement.clientHeight || 0;
    const scrollHeight = nextElement?.scrollHeight || document.documentElement.scrollHeight || document.body.scrollHeight || 0;
    const maxScrollTop = Math.max(0, scrollHeight - clientHeight);
    return { scrollTop, clientHeight, scrollHeight, maxScrollTop };
  }, []);

  // 统一写入滚动位置，确保在不同容器场景都能正确生效
  const setPageScrollTop = useCallback((nextTop: number) => {
    if (typeof window === 'undefined') return;
    const normalizedTop = Math.max(0, nextTop);
    
    // 尽量覆盖全部滚动容器，减少在不同浏览器/容器中的定位误差
    const nextElement = document.getElementById('__next');
    if (nextElement) {
      const originalScrollBehavior = nextElement.style.scrollBehavior;
      nextElement.style.scrollBehavior = 'auto';
      nextElement.scrollTop = normalizedTop;
      nextElement.style.scrollBehavior = originalScrollBehavior;
    }
    
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo({ top: normalizedTop, behavior: 'instant' as any } as ScrollToOptions);
    document.documentElement.scrollTop = normalizedTop;
    document.body.scrollTop = normalizedTop;
    document.documentElement.style.scrollBehavior = '';
  }, []);

  // 程序触发滚动时短暂屏蔽“触底自动加载”，防止恢复定位触发误加载
  const markProgrammaticScroll = useCallback(() => {
    if (typeof window === 'undefined') return;
    // 行为：短时间打标，避免恢复滚动触发列表自动加载
    programmaticScrollRef.current = true;
    if (programmaticScrollTimerRef.current) {
      window.clearTimeout(programmaticScrollTimerRef.current);
    }
    programmaticScrollTimerRef.current = window.setTimeout(() => {
      programmaticScrollRef.current = false;
      programmaticScrollTimerRef.current = null;
    }, 180);
  }, []);

  // URL 缺失 uid/uuid 时补齐，保证当前页参数完整且可分享
  // 首次进入如果 URL 没带 uid/uuid，会补齐到 query，保证页面语义一致
  useEffect(() => {
    if (!router.isReady) return;
    const uid = account?.id;
    const uuid = account?.uuid;
    if (!uid || !uuid) return;

    const currentUid = Array.isArray(router.query.uid) ? router.query.uid[0] : router.query.uid;
    if (currentUid) return;

    const internalPathname = router.pathname || '/user-center';
    const externalPathname = typeof window !== 'undefined' ? window.location.pathname : internalPathname;
    router.replace({
      pathname: internalPathname,
      query: {
        ...router.query,
        uid: String(uid),
        uuid: String(uuid),
      },
    }, {
      pathname: externalPathname,
      query: {
        ...router.query,
        uid: String(uid),
        uuid: String(uuid),
      },
    }, { shallow: true });
  }, [router.isReady, router.query, account?.id, account?.uuid]);

  // 解析 URL 参数并设置 activeTab：
  // do=thread/likes/favorite/mynotice 映射到对应 tab
  // ac=setting 直接拉起编辑资料弹窗
  useEffect(() => {
    if (!router.isReady) return;
    // 非本人页不跟随 do/ac 切换 tab，避免误聚焦
    if (!isSelfView) {
      setIsTabInitializedFromQuery(true);
      return;
    }

    const { do: action, ac } = router.query;
    const actionValue = Array.isArray(action) ? action[0] : action;
    const acValue = Array.isArray(ac) ? ac[0] : ac;
    
    let newTab: string | null = null;

    if (acValue === 'setting') {
      setShowEditProfileModal(true);
      const newQuery = { ...router.query };
      delete newQuery.ac;
      const internalPathname = router.pathname || '/user-center';
      const externalPathname = typeof window !== 'undefined' ? window.location.pathname : internalPathname;
      router.replace({ pathname: internalPathname, query: newQuery }, { pathname: externalPathname, query: newQuery }, { shallow: true });
    } else if (actionValue === 'thread') {
      newTab = 'myPosts';
    } else if (actionValue === 'likes') {
      newTab = 'myLikes';
    } else if (actionValue === 'favorite') {
      newTab = 'myCollections';
    } else if (actionValue === 'mynotice') {
      newTab = 'messages';
    }
    
    // tab 切换前先记录旧 tab 滚动位置，便于回来时复位
    if (newTab) {
      setActiveTab((prevTab) => {
        if (newTab !== prevTab) {
          const { scrollTop, maxScrollTop } = getPageScrollMetrics();
          const offsetFromBottom = Math.max(0, maxScrollTop - scrollTop);
          const isScrollable = maxScrollTop > 0;
          const isNearBottom = isScrollable && offsetFromBottom <= 8 && scrollTop > 0;
          tabScrollStateMapRef.current[prevTab] = {
            top: scrollTop,
            offsetFromBottom,
            isBottomAnchored: isNearBottom,
          };
          return newTab as string;
        }
        return prevTab;
      });
    }
    setIsTabInitializedFromQuery(true);
  }, [router.isReady, isSelfView, getPageScrollMetrics]);

  // tab 变化回写 URL（do 参数），并保持 shallow + scroll:false，避免回到顶部
  useEffect(() => {
    if (!router.isReady || !isSelfView || !isTabInitializedFromQuery) return;

    const currentQuery = { ...router.query };
    const hasAc = Object.prototype.hasOwnProperty.call(currentQuery, 'ac');
    delete currentQuery.ac;
    let newAction = '';

    switch (activeTab) {
      case 'myPosts':
        newAction = 'thread';
        break;
      case 'myLikes':
        newAction = 'likes';
        break;
      case 'myCollections':
        newAction = 'favorite';
        break;
      case 'messages':
        newAction = 'mynotice';
        break;
      default:
        break;
    }

    // 兼容 query 参数可能是 string[] 的场景，只取第一个 do 值参与比较
    const currentDo = Array.isArray(currentQuery.do) ? currentQuery.do[0] : currentQuery.do;
    // 仅当有目标动作且与当前 do 不一致时，才执行 URL 回写，避免无效路由跳转
    const shouldUpdateDo = Boolean(newAction) && currentDo !== newAction;
    const internalPathname = router.pathname || '/user-center';
    const externalPathname = typeof window !== 'undefined' ? window.location.pathname : internalPathname;

    if (shouldUpdateDo) {
      const newQuery = { ...currentQuery, do: newAction };
      // shallow + scroll:false：避免整页重取与滚动重置
      router.push({ pathname: internalPathname, query: newQuery }, { pathname: externalPathname, query: newQuery }, { shallow: true, scroll: false });
      return;
    }

    // do 已正确时，仅清理临时 ac 参数；replace 不会新增历史记录
    if (hasAc) {
      router.replace({ pathname: internalPathname, query: currentQuery }, { pathname: externalPathname, query: currentQuery }, { shallow: true, scroll: false });
    }
  }, [activeTab, router.isReady, isSelfView, isTabInitializedFromQuery]);

  // tab 切换后的滚动恢复：多次重试，抵消 Next/router 与 DOM 更新时序差
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const prevTab = prevActiveTabRef.current;
    if (prevTab === activeTab) return;
    
    // 新 tab 只负责恢复，不再重复保存（保存发生在切换前）
    const restoreState = tabScrollStateMapRef.current[activeTab];
    const applyRestore = () => {
      markProgrammaticScroll();
      if (!restoreState) {
        suppressAutoLoadUntilUserScrollRef.current = false;
        restoredScrollTopRef.current = null;
        setPageScrollTop(0);
        return;
      }
      
      // 强化恢复：同一目标位点进行多轮写入，提升稳定性
      const targetTop = restoreState.top;
      restoredScrollTopRef.current = targetTop;
      suppressAutoLoadUntilUserScrollRef.current = true;
      
      // 第一次：立即恢复
      setPageScrollTop(targetTop);
      
      // 第二次：下一帧恢复
      requestAnimationFrame(() => {
        setPageScrollTop(targetTop);
        
        // 第三次：宏任务恢复
        setTimeout(() => {
          setPageScrollTop(targetTop);
          
          // 第四次：最终兜底恢复
          setTimeout(() => {
            setPageScrollTop(targetTop);
          }, 50);
        }, 10);
      });
    };
    
    applyRestore();
    prevActiveTabRef.current = activeTab;

    return () => {
      // 保留清理位，便于后续补充副作用释放
    };
  }, [activeTab, getPageScrollMetrics, setPageScrollTop, markProgrammaticScroll]);

  // 卸载时清理程序滚动计时器
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && programmaticScrollTimerRef.current) {
        window.clearTimeout(programmaticScrollTimerRef.current);
      }
    };
  }, []);

  // 展示态账户对象：统一融合 SSR 数据、store 数据与 profilePatch
  const displayAccount = useMemo(() => {
    // 无 initialUserInfo 时，使用登录态兜底（通常是本人页）
    if (!initialUserInfo) {
      const acc = (account as any) || {};
      const accAvatar = profilePatch?.avatarUrl || acc?.avatar?.large || DefaultAvatar;
      return {
        ...acc,
        name: profilePatch?.name || acc?.name,
        avatar: {
          large: accAvatar,
          medium: accAvatar,
          small: accAvatar,
        }
      };
    }

    // 有 initialUserInfo 时，以其为主并注入 patch
    const base = isSelfView ? (account as any) : {};

    const avatarUrl = profilePatch?.avatarUrl || initialUserInfo.avatar || (isSelfView ? (account as any)?.avatar?.large : "") || DefaultAvatar;
    const mergedAvatar = {
      large: avatarUrl,
      medium: avatarUrl,
      small: avatarUrl,
    };
    return {
      ...base,
      id: initialUserInfo.id,
      uuid: initialUserInfo.uuid,
      name: profilePatch?.name || initialUserInfo.name,
      avatar: mergedAvatar,
      credits: initialUserInfo.credits || 0,
      devices: initialDevices || [],
      deviceCount: initialDevices?.length || 0,
      group: initialUserInfo?.group,
      flightDistance: initialUserInfo.total_distance || 0,
      hide_distance: initialUserInfo.hide_distance,
      admin_view: initialUserInfo.admin_view,
      rank: initialUserInfo.rank,
    };
  }, [account, initialUserInfo, initialDevices, isSelfView, profilePatch]);

  // 列表域逻辑下沉：分页、合并、去重、消息同步、列表 patch
  const {
    postsData,
    postsLoading,
    likesData,
    likesLoading,
    likesInitialized,
    collectionsData,
    collectionsLoading,
    collectionsInitialized,
    messagesData,
    messagesLoading,
    messagesInitialized,
    drafts,
    publishedPosts,
    showDrafts,
    showPosts,
    handleItemUpdate,
  } = useUserCenterPcListLogic({
    initialWorks,
    initialUserInfo,
    account,
    userStats,
    displayAccount,
    isSelfView,
    activeTab,
    dispatch,
    programmaticScrollRef,
    suppressAutoLoadUntilUserScrollRef,
    restoredScrollTopRef,
    threadStatePatchRef,
  });

  // 兜底权限：本人主页不可访问时重定向到拒绝页
  useEffect(() => {
    if (account && account.home_page_accessible_by_self === false) {
      window.location.href = '/pro/access-denied';
    }
  }, [account]);

  // 管理员视角标记
  const isAdminView = useMemo(() => {
    return (account as any)?.role === ACCOUNT_ROLE.ADMIN;
  }, [account]);

  // 页面级 SLS：SSR 耗时 / 登录信息 API 关键指标上报
  useUserCenterSlsReport({
    reportSlsData,
    initialSsrDuration,
    initialLoginInfoSls,
    isSelfView,
    terminalType: 'pc',
    accountId: Number((account as any)?.id),
    queryUid: router.query?.uid,
  });

  // 刷新全局 account：编辑资料成功后与 header 等区域保持一致
  const refreshGlobalAccount = useCallback(async () => {
    try {
      const latestAccount = await getUserLoginInfo(false, {}, Date.now());
      dispatch({
        namespace: namespace.app,
        type: ActionTypeValues.UPDATE_ACCOUNT,
        payload: latestAccount ? { ...latestAccount, status: PromiseState.FULFILLED } : latestAccount,
      });
    } catch (e) {
      return;
    }
  }, [dispatch]);

  // 积分显示逻辑：初始值来自 SSR / displayAccount，后续可被兑换回调实时改写
  const [points, setPoints] = useState<number>(userStats?.points || displayAccount?.credits || 0);
  // 超大积分显示上限处理
  const displayPoints = points > USER_CENTER_MAX_POINTS_VALUE ? `${USER_CENTER_MAX_POINTS_VALUE}+` : points;
  // 登录/访问奖励提示文案（仅展示一次）
  const creditTipText = resolveUserCenterCreditTip(initialUserInfo?.login_credit_added, initialUserInfo?.visit_credit_added);
  const displayName = displayAccount?.name || '';
  // 管理员模式下名称超长使用 tooltip，避免布局抖动
  const shouldShowAdminNameTooltip = isAdminView && displayName.length > 12;

  // 同步积分来源：优先 userStats，其次 displayAccount
  useEffect(() => {
    if (userStats?.points !== undefined) {
      setPoints(userStats.points);
    } else if (displayAccount?.credits !== undefined) {
      setPoints(displayAccount.credits);
    }
  }, [userStats?.points, displayAccount?.credits]);

  // 进入页时如果有积分奖励提示，弹 toast（一次性）
  useEffect(() => {
    if (!creditTipText || hasShownCreditTipRef.current) return;
    hasShownCreditTipRef.current = true;
    Toast.show({
      content: creditTipText,
      style: { top: '26%' },
      rootClassName: 'toastLineHeightUnset',
    });
  }, [creditTipText]);

  // 用户组 icon 变更后重置加载错误标记，允许重新渲染
  useEffect(() => {
    setBadgeIconError(false);
  }, [displayAccount?.group?.icon]);

  // 以 displayAccount 的 hide_distance 为准回填本地开关状态
  useEffect(() => {
    if (displayAccount?.hide_distance !== undefined) {
      setHideDistance(displayAccount.hide_distance);
    }
  }, [displayAccount?.hide_distance]);

  // 动作域逻辑下沉：侧栏、设备入口、发帖、里程开关、埋点
  const {
    handleToggleFlightVisibility,
    handlePost,
    handlePointsExchangeClick,
    handleDeviceIconClick,
    handleEditProfileClick,
    sidebarOptions,
    deviceCount,
    displayFlightDistance,
    adminUid,
    adminEmail,
    adminIp,
    adminLocation,
    adminLastLoginTime,
    previewDevices,
    showMoreDevices,
    showFlightInfo,
  } = useUserCenterPcActions({
    account,
    isSelfView,
    displayAccount,
    t,
    track,
    TRACK_EVENTS,
    permission,
    activeTab,
    getPageScrollMetrics,
    tabScrollStateMapRef,
    setActiveTab,
    setShowPointsExchangeModal,
    setShowEditProfileModal,
    hideDistance,
    setHideDistance,
    updating,
    setUpdating,
  });

  // 访客页仅显示“帖子”tab；本人页显示完整四个 tab
  const visibleTabKeys = isSelfView
    ? ['myPosts', 'myLikes', 'myCollections', 'messages']
    : ['myPosts'];

  // SEO 文案根据当前展示用户名动态拼接
  const seoTitle = t('user_center_meta_title', { name: displayAccount?.name || account?.name || '' });
  const seoDescription = t('user_center_meta_description', { name: displayAccount?.name || account?.name || '' });
  const seoKeywords = t('user_center_meta_keywords');

  // 页面只做装配：将渲染与交互透传给布局组件
  return (
    <UserCenterPcLayout
      t={t}
      seoTitle={seoTitle}
      seoDescription={seoDescription}
      seoKeywords={seoKeywords}
      account={account!}
      navList={navList || []}
      saltkey={saltkey || ''}
      isSelfView={isSelfView}
      isAdminView={isAdminView}
      displayAccount={displayAccount}
      displayName={displayName}
      shouldShowAdminNameTooltip={shouldShowAdminNameTooltip}
      badgeIconError={badgeIconError}
      setBadgeIconError={setBadgeIconError}
      handleEditProfileClick={handleEditProfileClick}
      showFlightInfo={showFlightInfo}
      displayFlightDistance={displayFlightDistance}
      hideDistance={hideDistance}
      handleToggleFlightVisibility={handleToggleFlightVisibility}
      handlePointsExchangeClick={handlePointsExchangeClick}
      displayPoints={displayPoints}
      deviceCount={deviceCount}
      previewDevices={previewDevices}
      showMoreDevices={showMoreDevices}
      handleDeviceIconClick={handleDeviceIconClick}
      setShowDevicesModal={setShowDevicesModal}
      adminUid={adminUid}
      adminEmail={adminEmail}
      adminIp={adminIp}
      adminLocation={adminLocation}
      adminLastLoginTime={adminLastLoginTime}
      sidebarOptions={sidebarOptions}
      activeTab={activeTab}
      handlePost={handlePost}
      visibleTabKeys={visibleTabKeys}
      // tab 面板渲染函数：每个 tab 进入统一渲染器
      renderTabPanel={(tabKey) =>
        renderPcTabContent({
          tabKey,
          isSelfView,
          t,
          showDrafts,
          showPosts,
          drafts,
          publishedPosts,
          postsLoading,
          handleItemUpdate,
          likesInitialized,
          likesData,
          likesLoading,
          collectionsInitialized,
          collectionsData,
          collectionsLoading,
          messagesInitialized,
          messagesData,
          messagesLoading,
          setShowPointsExchangeModal,
        })
      }
      // 内容空态时切换主背景样式
      transparentMain={isPcTabContentEmpty({
        activeTab,
        showDrafts,
        showPosts,
        likesInitialized,
        likesData,
        likesLoading,
        collectionsInitialized,
        collectionsData,
        collectionsLoading,
        messagesInitialized,
        messagesData,
        messagesLoading,
      })}
      showDevicesModal={showDevicesModal}
      initialDevices={initialDevices}
      showPointsExchangeModal={showPointsExchangeModal}
      setShowPointsExchangeModal={setShowPointsExchangeModal}
      setPoints={setPoints}
      showEditProfileModal={showEditProfileModal}
      setShowEditProfileModal={setShowEditProfileModal}
      initialUserInfo={initialUserInfo}
      profilePatch={profilePatch}
      refreshGlobalAccount={refreshGlobalAccount}
      setProfilePatch={setProfilePatch}
      // 顶部 Header 积分变化回调（仅本人页生效）
      onCreditsChange={(newCredits) => {
        if (isSelfView) {
          setPoints(newCredits);
        }
      }}
    />
  );
};

// SSR 已下沉到 pc/ssr.ts，这里保持 next.js 约定导出
export const getServerSideProps = getUserCenterPcServerSideProps;

export default UserCenter;
